import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import cookies from 'js-cookie';
import { baseUrls, cookieNames } from '@monorepo/common';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';
import type { BreweryAccess } from './contracts';

export const breweryLoginUrl = (returnUrl: string) =>
  `${baseUrls.login.replace(/\/$/, '')}/?returnUrl=${encodeURIComponent(returnUrl)}`;
export const breweryReturnUrl = () =>
  `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
let redirectingToLogin = false;
export function expireBrewerySession() {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  useAuthStore.getState().logout();
  cookies.remove(cookieNames.refresh_token);
  window.location.assign(breweryLoginUrl(breweryReturnUrl()));
}
export class BreweryApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'BreweryApiError';
  }
}
// Accept HTTP responses here so brewery authorization is handled before the shared
// interceptor's generic re-login behavior. The request interceptor still reads
// the current bearer token for every request (including file uploads).
export async function breweryRequest<T>(
  config: AxiosRequestConfig,
): Promise<T> {
  let response: AxiosResponse<T>;
  try {
    response = await apiClient.request<T>({
      ...config,
      validateStatus: () => true,
    });
  } catch {
    // No HTTP response at all (offline, DNS, CORS, aborted connection).
    throw new BreweryApiError(
      0,
      "We couldn't reach the server. Check your connection and try again.",
    );
  }
  if (response.status >= 200 && response.status < 300) return response.data;
  const body = response.data as unknown as {
    message?: string;
    error?: string;
    errors?: Record<string, string[]>;
  };
  const fieldMessage = Object.values(body?.errors || {})
    .flat()
    .join(' ');
  // Proxies return HTML error pages; only short plain-text bodies are shown as-is.
  const text =
    typeof response.data === 'string' ? response.data.trim() : '';
  const message =
    text && text.length <= 300 && !/^</.test(text)
      ? text
      : body?.message ||
        body?.error ||
        fieldMessage ||
        (response.status === 404
          ? 'This brewery or item could not be found.'
          : response.status === 403
            ? 'Your brewery access changed. Checking your permissions...'
            : 'Unable to complete the request. Please try again.');
  if (response.status === 401) expireBrewerySession();
  if (response.status === 403)
    window.dispatchEvent(new Event('breweryAccessRevoked'));
  throw new BreweryApiError(response.status, message, body?.errors);
}
export const breweryPath = (guid: string, section = '') =>
  `/Breweries/${encodeURIComponent(guid)}${section ? `/${section}` : ''}`;
export const getBreweryAccess = (guid: string) =>
  breweryRequest<BreweryAccess>({ url: '/Breweries/access', params: { guid } });
export const sendBreweryVerification = () =>
  breweryRequest({
    method: 'POST',
    url: '/Breweries/verify-email',
    data: { returnUrl: breweryReturnUrl() },
  });
